import { SignalIcon, ClockIcon } from '@heroicons/react/24/outline';

/**
 * Live | Recorded — the Live Wall's title-bar switch between watching now and playing back.
 *
 * Two segments of one control rather than a button, because the two are the same wall seen at
 * two times: the operator should always be able to see which one they are on.
 */
export type WallMode = 'live' | 'recorded';

export interface WallModeToggleProps {
  mode: WallMode;
  onChange: (mode: WallMode) => void;
  /** Recorded needs recordings.view; without it the segment is not offered. */
  canRecorded?: boolean;
  /** Disable switching (mid-edit on the live wall — leaving would discard the edit). */
  disabled?: boolean;
  className?: string;
}

const seg = 'flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 text-xs sm:text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50';

export default function WallModeToggle({ mode, onChange, canRecorded = true, disabled = false, className = '' }: WallModeToggleProps) {
  if (!canRecorded) return null;
  return (
    <div data-tour="wall-mode-toggle" role="tablist" aria-label="Wall mode" className={`inline-flex overflow-hidden rounded-lg border border-border bg-muted ${className}`}>
      <button
        role="tab"
        aria-selected={mode === 'live'}
        disabled={disabled}
        onClick={() => mode !== 'live' && onChange('live')}
        className={`${seg} ${mode === 'live' ? 'bg-brand text-white' : 'text-muted-foreground hover:text-foreground'}`}
      >
        <SignalIcon className="h-4 w-4" />
        <span>Live</span>
      </button>
      <button
        role="tab"
        aria-selected={mode === 'recorded'}
        disabled={disabled}
        onClick={() => mode !== 'recorded' && onChange('recorded')}
        className={`${seg} ${mode === 'recorded' ? 'bg-brand text-white' : 'text-muted-foreground hover:text-foreground'}`}
      >
        <ClockIcon className="h-4 w-4" />
        <span>Recorded</span>
      </button>
    </div>
  );
}
